import {
  GetShowTranslationModal,
  GetShowPreviewDocument,
  GetCurrentDocumentsDialog,
  GetCreateCollectionPopup,
  GetAddToCollectionPopup,
  GetGlossaryModal,
  GetWarningPopup,
  GetShowFilters,
  GetShowFiles,
  GetSelectedExternalIdForTranslate,
  GetReferenceNameForTranslate,
  GetSharedLinkCollection,
  GetLayoutsListOpened,
  GetMediaPanelOpened,
  GetShowPreviewAuthorDocument,
  GetReferencesListOpened,
  GetTranslationType,
  GetTranslationCallback
} from '~/constants/getters';
import {
  ShowTranslationModal,
  HideTranslationModal,
  ShowPreviewDocument,
  HidePreviewDocument,
  SetShowAddToCollectionPopup,
  SetGlossaryModal,
  SetWarningPopup,
  SetShowFilters,
  SetShowFiles,
  SetSharedLinkCollectionPopup,
  SetMediaPanelOpened,
  ShowPreviewAuthorDocument,
  HidePreviewAuthorDocument,
} from '~/constants/actions';
import {
  SetShowTranslationModal,
  SetShowPreviewDocument,
  SetCurrentDocumentsDialog,
  SetCreateCollectionPopup,
  SetLayoutsListOpened,
  SetShowPreviewAuthorDocument,
  SetReferencesListOpened,
} from '~/constants/mutations';
import { TRANSLATION_TYPE } from '~/constants/documents';

const defaultState = () => ({
  currentDocumentsDialog: null,
  showTranslationModal: {
    type: TRANSLATION_TYPE.document,
    open: false,
    selectedExternalId: '',
    referenceName: '',
    callback: () => { },
  },
  showGlossaryModal: false,
  showFiles: false,
  showPreviewDocument: {
    open: false,
    startPage: 1,
  },
  showPreviewAuthorDocument: false,
  createCollectionPopup: {
    open: false,
    collection: {},
  },
  showAddToCollectionPopup: { open: false, document: {} },
  shareCollectionPopup: { open: false, collectionId: {} },
  warningPopup: false,
  showFilters: false,
  layoutsListOpened: false,
  referencesListOpened: { open: false, editable: false },
  mediaPanelOpened: false,
});

const state = defaultState;

const getters = {
  [GetShowTranslationModal]: (state) => state.showTranslationModal.open,
  [GetSelectedExternalIdForTranslate]: (state) => state.showTranslationModal.selectedExternalId,
  [GetTranslationCallback]: (state) => state.showTranslationModal.callback,
  [GetReferenceNameForTranslate]: (state) => state.showTranslationModal.referenceName,
  [GetTranslationType]: (state) => state.showTranslationModal.type,
  [GetShowPreviewDocument]: (state) => state.showPreviewDocument,
  [GetShowPreviewAuthorDocument]: (state) => state.showPreviewAuthorDocument,
  [GetCurrentDocumentsDialog]: (state) => state.currentDocumentsDialog,
  [GetCreateCollectionPopup]: (state) => state.createCollectionPopup,
  [GetAddToCollectionPopup]: (state) => state.showAddToCollectionPopup,
  [GetSharedLinkCollection]: (state) => state.shareCollectionPopup,
  [GetGlossaryModal]: (state) => state.showGlossaryModal,
  [GetWarningPopup]: (state) => state.warningPopup,
  [GetShowFilters]: (state) => state.showFilters,
  [GetShowFiles]: (state) => state.showFiles,
  [GetLayoutsListOpened]: (state) => state.layoutsListOpened,
  [GetReferencesListOpened]: (state) => state.referencesListOpened,
  [GetMediaPanelOpened]: (state) => state.mediaPanelOpened,
};

const mutations = {
  [SetShowFilters](state, data) {
    state.showFilters = data;
  },
  [SetShowFiles](state, data) {
    state.showFiles = data;
  },
  [SetShowPreviewAuthorDocument](state, data) {
    state.showPreviewAuthorDocument = data;
  },
  [SetShowTranslationModal](state, { open, selectedExternalId, referenceName, callback, type }) {
    state.showTranslationModal = {
      ...state.showTranslationModal,
      open,
      selectedExternalId,
      referenceName,
      callback,
      type,
    };
  },
  [SetShowPreviewDocument](state, { open, startPage }) {
    state.showPreviewDocument = {
      ...state.showPreviewDocument,
      open,
      startPage,
    };
  },
  [SetCurrentDocumentsDialog](state, data) {
    state.currentDocumentsDialog = data;
  },
  [SetCreateCollectionPopup](state, { open, collection }) {
    state.createCollectionPopup = {
      ...state.createCollectionPopup,
      open,
      collection,
    };
  },
  [SetShowAddToCollectionPopup](state, { open, document }) {
    state.showAddToCollectionPopup = {
      ...state.showAddToCollectionPopup,
      open,
      document,
    };
  },
  [SetSharedLinkCollectionPopup](state, { open, collectionId }) {
    state.shareCollectionPopup = {
      ...state.shareCollectionPopup,
      open,
      collectionId,
    };
  },
  [SetGlossaryModal](state, data) {
    state.showGlossaryModal = data;
  },
  [SetWarningPopup](state, data) {
    state.warningPopup = data;
  },
  [SetLayoutsListOpened](state, data) {
    state.layoutsListOpened = data;
  },
  [SetReferencesListOpened](state, { open, editable }) {
    state.referencesListOpened = { ...state.referencesListOpened, open, editable };
  },
  [SetMediaPanelOpened](state, data) {
    state.mediaPanelOpened = data;
  },
};

const actions = {
  [ShowTranslationModal]: ({ commit }, { referenceId, referenceName, type, callback }) => {
    commit(SetShowTranslationModal, { open: true, selectedExternalId: referenceId, referenceName, type, callback });
  },
  [HideTranslationModal]: ({ commit }) => {
    commit(SetShowTranslationModal, { open: false, selectedExternalId: '', referenceName: '', callback: () => { }, type: TRANSLATION_TYPE.document });
  },
  [ShowPreviewDocument]: ({ commit }, startPage) => {
    commit(SetShowPreviewDocument, { open: true, startPage });
  },
  [ShowPreviewAuthorDocument]: ({ commit }, data) => {
    commit(SetShowPreviewAuthorDocument, data);
  },
  [HidePreviewDocument]: ({ commit }) => {
    commit(SetShowPreviewDocument, { open: false, startPage: 1 });
  },
  [ShowPreviewAuthorDocument]: ({ commit }) => {
    commit(SetShowPreviewAuthorDocument, true);
  },
  [HidePreviewAuthorDocument]: ({ commit }) => {
    commit(SetShowPreviewAuthorDocument, false);
  },
  [SetCurrentDocumentsDialog]: ({ commit }, data) => {
    commit(SetCurrentDocumentsDialog, data);
  },
  [SetCreateCollectionPopup]: ({ commit }, data) => {
    commit(SetCreateCollectionPopup, data);
  },
  [SetShowAddToCollectionPopup]: ({ commit }, data) => {
    commit(SetShowAddToCollectionPopup, data);
  },
  [SetSharedLinkCollectionPopup]: ({ commit }, data) => {
    commit(SetSharedLinkCollectionPopup, data);
  },
  [SetGlossaryModal]: ({ commit }, data) => {
    commit(SetGlossaryModal, data);
  },
  [SetWarningPopup]: ({ commit }, data) => {
    commit(SetWarningPopup, data);
  },
  [SetShowFilters]: ({ commit }, data) => {
    commit(SetShowFilters, data);
  },
  [SetShowFiles]: ({ commit }, data) => {
    commit(SetShowFiles, data);
  },
  [SetLayoutsListOpened]: ({ commit }, data) => {
    commit(SetLayoutsListOpened, data);
  },
  [SetReferencesListOpened]: ({ commit }, data) => {
    commit(SetReferencesListOpened, data);
  },
  [SetMediaPanelOpened]: ({ commit }, data) => {
    commit(SetMediaPanelOpened, data);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
